import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { provideServiceWorker } from '@angular/service-worker';
import { 
  MsalService, 
  MsalGuard, 
  MsalInterceptor,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration
} from '@azure/msal-angular';
import { 
  IPublicClientApplication,
  PublicClientApplication, 
  InteractionType, 
  BrowserCacheLocation,
  LogLevel 
} from '@azure/msal-browser';

import { routes } from './app.routes';
import { ConfigService } from './services/config.service';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function MSALInstanceFactory(): IPublicClientApplication {
  const configService = new ConfigService();
  return new PublicClientApplication({
    auth: {
      clientId: configService.clientId,
      authority: configService.authority,
      redirectUri: configService.redirectUri,
      postLogoutRedirectUri: configService.postLogoutRedirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string) => {
          switch (level) {
            case LogLevel.Error:
              console.error(message);
              return;
            case LogLevel.Info:
              console.info(message);
              return;
            case LogLevel.Verbose:
              console.debug(message);
              return;
            case LogLevel.Warning:
              console.warn(message);
              return;
            default:
              return;
          }
        }
      }
    }
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideNoopAnimations(),
    provideToastr({
      closeButton: true,
      progressBar: true,
      positionClass: 'toast-top-right',
      preventDuplicates: true
    }),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: (configService: ConfigService) => ({
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: configService.scopes
        }
      } as MsalGuardConfiguration),
      deps: [ConfigService]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: (configService: ConfigService) => ({
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          [configService.apiUri, configService.scopes]
        ])
      } as MsalInterceptorConfiguration),
      deps: [ConfigService]
    },
    // Conditionally provide service worker based on config
    ...((() => {
      const configService = new ConfigService();
      return configService.serviceWorkerEnabled ? [
        provideServiceWorker('ngsw-worker.js', {
          enabled: true,
          registrationStrategy: 'registerWhenStable:30000'
        })
      ] : [];
    })())
  ]
};
